import type { FC } from 'react';
import React from 'react';
import { useParams } from 'react-router';

import { faArrowDown } from '@fortawesome/free-solid-svg-icons/faArrowDown';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faKeyboard } from '@fortawesome/free-solid-svg-icons/faKeyboard';
import { faQuestion } from '@fortawesome/free-solid-svg-icons/faQuestion';
import { faUser } from '@fortawesome/free-solid-svg-icons/faUser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';

import { NavLinkButton } from '@shared/components/bootstrap/button';
import { Panel } from '@shared/components/bootstrap/panel';

import { Formatter as PhoneFormatter } from '@admin/components/phone/formatter';
import { voicemailsURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useVoicemailsDetailsQuery } from '@admin/schema';

export const VoicemailsDetails: FC = () => {
  useTitle('Voicemail');
  const { id } = useParams<{ id: string }>();
  const { data } = useVoicemailsDetailsQuery({ client, variables: { id } });
  const voicemail = data?.voicemail;
  if (!voicemail) return null;

  const startAt = voicemail.phoneCall.startAt ? DateTime.fromISO(voicemail.phoneCall.startAt) : null;
  const endAt = voicemail.phoneCall.endAt ? DateTime.fromISO(voicemail.phoneCall.endAt) : null;
  const duration = startAt && endAt ? endAt.diff(startAt) : undefined;

  return (
    <>
      <Panel>
        <Panel.Header>
          <h3 className="panel-title">Details</h3>
        </Panel.Header>
        <Panel.Body>
          <p>
            <strong>Phone: </strong>
            <span>
              <PhoneFormatter number={voicemail.phoneCall.phone.number} />
            </span>
          </p>
          <p>
            <strong>System Phone: </strong>
            <span>
              <PhoneFormatter number={voicemail.phoneCall.systemPhone.number} />
            </span>
          </p>
          <p>
            <strong>Direction: </strong>
            <span>{voicemail.phoneCall.direction}</span>
          </p>
          <p>
            <strong>Start at: </strong>
            {startAt?.toLocaleString(DateTime.DATETIME_MED) ?? '-'}
          </p>
          <p>
            <strong>End at: </strong>
            {endAt?.toLocaleString(DateTime.DATETIME_MED) ?? '-'}
          </p>
        </Panel.Body>
        <Panel.Footer>
          <NavLinkButton kind="default" to={voicemailsURL()}>
            Back
          </NavLinkButton>
        </Panel.Footer>
      </Panel>

      <Panel>
        <Panel.Header>
          <h3 className="panel-title">Timeline</h3>
        </Panel.Header>
        <Panel.Body>
          <div className="timeline">
            <div className="timeline-header">
              <div className="timeline-header-title bg-info">{duration?.toFormat('hh:mm:ss') ?? '-'}</div>
            </div>

            {voicemail.phoneCall.activities.map((activity) => (
              <div key={activity.id} className="timeline-entry">
                <div className="timeline-stat">
                  <div className="timeline-icon bg-success">
                    <FontAwesomeIcon
                      icon={(() => {
                        switch (activity.verb) {
                          case 'call:inbound':
                            return faArrowRight;
                          case 'call:ivr':
                            return faKeyboard;
                          case 'call:voicemail':
                            return faEnvelope;
                          case 'call:operator':
                            return faUser;
                          case 'call:ended':
                            return faArrowDown;
                          default:
                            return faQuestion;
                        }
                      })()}
                    />
                  </div>
                  <div className="timeline-time">
                    {startAt
                      ? DateTime.fromISO(activity.createdAt ?? activity.updatedAt)
                          .diff(startAt)
                          .toFormat('hh:mm:ss')
                      : '-'}
                  </div>
                </div>
                <div className="timeline-label">
                  <h4 className="mar-no pad-btm">{activity.verb}</h4>
                  <p>{activity.message}</p>
                </div>
              </div>
            ))}
          </div>
        </Panel.Body>
      </Panel>
    </>
  );
};
